/**
 * Affiliate-link referral capture.
 *
 * Lifecycle:
 *   1. `captureReferralFromUrl()` runs once at app boot (from `main.tsx`,
 *      BEFORE Auth0Provider mounts) and reads `?ref=<code>` off the
 *      landing URL. Valid codes are persisted to localStorage with a
 *      capture timestamp and the param is stripped from the URL so a
 *      refresh doesn't re-fire the capture.
 *   2. On `POST /auth/callback` the auth service reads back the stored
 *      ref and includes it in the body. The server only applies it
 *      when creating a NEW account; existing-account callbacks ignore
 *      it. Unknown or malformed codes never block signup.
 *   3. `clearStoredReferral()` runs after a `created: true` callback so
 *      the ref doesn't linger and accidentally attribute a future
 *      different account on the same browser.
 *
 * Attribution window: 30 days from capture. Older entries are treated
 * as expired and quietly dropped. Industry standard; keeps stale clicks
 * from over-attributing accounts that signed up months later for
 * unrelated reasons.
 */

import { affiliateService } from '@/services/affiliate.service';

const STORAGE_KEY = 'ollie:referral';
const QUERY_PARAM = 'ref';
const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * A `?ref=` value is valid if it's EITHER the legacy 10-hex `affiliate_code`
 * OR a vanity `affiliate_slug` (3–32 chars, lowercase alnum + interior
 * hyphens). The hex code is a subset of this shape, so links shared before
 * vanity slugs existed still validate unchanged. Kept in lockstep with the
 * server: `auth-callback.dto.ts` (the value forwarded on signup) and
 * `isValidAffiliateSlug` / the DB CHECK. Values are lowercased before this
 * check runs.
 */
const REF_MIN_LENGTH = 3;
const REF_MAX_LENGTH = 32;
const REF_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function isValidRef(code: string): boolean {
  return code.length >= REF_MIN_LENGTH && code.length <= REF_MAX_LENGTH && REF_PATTERN.test(code);
}

interface StoredReferral {
  code: string;
  /** Unix millis. Used to enforce the 30-day attribution window. */
  capturedAt: number;
}

function isStoredReferral(value: unknown): value is StoredReferral {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.code === 'string' &&
    isValidRef(v.code) &&
    typeof v.capturedAt === 'number' &&
    Number.isFinite(v.capturedAt)
  );
}

/**
 * Read `?ref=<code>` from the current URL. If present and well-formed,
 * persist it to localStorage and strip the param. Safe to call even
 * when no `?ref` is present (no-op).
 *
 * Idempotent — capturing the same code twice just refreshes the
 * timestamp. We intentionally let a fresh capture overwrite an older
 * stored value: the most recent affiliate to send the user wins.
 */
export function captureReferralFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(QUERY_PARAM);
  if (!raw) return;

  // Strip the param unconditionally — even if it's malformed, we don't
  // want it sticking around to cause confusion on refresh.
  url.searchParams.delete(QUERY_PARAM);
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);

  const code = raw.toLowerCase();
  if (!isValidRef(code)) return;

  try {
    const payload: StoredReferral = { code, capturedAt: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Private-mode browsers or quota errors — silently skip. Referrals
    // are nice-to-have, never load-bearing.
  }

  // Fire-and-forget click beacon so the affiliate's dashboard can show
  // unique-visitor clicks + a conversion rate. The server dedupes by hashed
  // IP per month, so re-firing on a repeat visit is harmless. Swallow every
  // error — telemetry must never break the landing. Runs even if the
  // localStorage write above failed; the click still happened.
  void affiliateService.recordClick(code).catch(() => {});
}

/**
 * Read the stored referral code, if any. Returns `null` when:
 *   * nothing is stored,
 *   * the stored value is corrupt,
 *   * or the capture is older than the 30-day attribution window.
 * Expired entries are evicted as a side effect of being read.
 */
export function getStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isStoredReferral(parsed)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (Date.now() - parsed.capturedAt > ATTRIBUTION_WINDOW_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.code;
  } catch {
    return null;
  }
}

/**
 * Remove the stored referral. Called after a successful account-creating
 * `POST /auth/callback` so a different user on the same browser later
 * doesn't accidentally get attributed to the same referrer.
 */
export function clearStoredReferral(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}
